/**
 * テキストの行数を自動制限する機能
 *
 * 仕様
 * - ist-cardLarge__contentInner: 親要素
 * - ist-cardLarge__titleText: タイトル
 * - ist-cardLarge__mainText: 本文（複数のpタグが含まれる想定）
 * - ist-cardLarge__labels: ラベルリスト
 * - ist-cardLarge__label: ラベル
 *
 * 使い方
 * <div class="ist-cardLarge__contentInner">
 *   <div class="ist-cardLarge__titleText">タイトル</div>
 *   <div class="ist-cardLarge__mainText">
 *      <p>本文1</p>
 *      <p>本文2</p>
 *      <!-- 複数のpタグがある場合 -->
 *   </div>
 *   <ul class="ist-cardLarge__labels">
 *     <li class="ist-cardLarge__label">ラベル</li>
 *     <li class="ist-cardLarge__label">ラベル</li>
 *     <li class="ist-cardLarge__label">ラベル</li>
 *     <li class="ist-cardLarge__label">ラベル</li>
 *     <li class="ist-cardLarge__label">ラベル</li>
 *   </ul>
 * </div>
 */

export class CardLargeLineClamp {
  // クラス名の定数
  private static readonly CLASSES = {
    CONTENT_INNER: 'ist-cardLarge__contentInner',
    TITLE_TEXT: 'ist-cardLarge__titleText',
    MAIN_TEXT: 'ist-cardLarge__mainText',
    LABELS: 'ist-cardLarge__labels',
    LABEL: 'ist-cardLarge__label',
  };

  // リサイズ監視用のオブザーバー
  private resizeObserver: ResizeObserver;

  // リサイズ処理のタイムアウトID
  private resizeTimeout: number | null = null;

  // 非表示用のクラス名
  private readonly NO_DISPLAY_CLASS = 'ist-js__cardLargeTextNodisplay';

  // デバッグモードのフラグ
  private debug: boolean;

  /**
   * コンストラクタ
   * @param options デバッグモードを有効にするオプション
   */
  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug || false;
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  /**
   * 初期化処理
   * 対象要素の監視を開始し、行数制限を適用する
   */
  private initialize(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardLargeLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resizeObserver.observe(contentInner);
      // ラベルリストにoverflow: hiddenを適用
      const labelsContainer = contentInner.querySelector(
        `.${CardLargeLineClamp.CLASSES.LABELS}`
      ) as HTMLElement;
      if (labelsContainer) {
        labelsContainer.style.overflow = 'hidden';
      }
    });

    this.updateAllLineClamps();
  }

  /**
   * 要素の実際の高さを取得する
   * @param element 測定対象の要素
   * @returns 要素の高さ(px)
   */
  private getActualHeight(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    const parent = element.parentElement;

    if (!parent) return 0;

    clone.style.cssText = `
      position: absolute !important;
      visibility: hidden !important;
      display: block !important;
      width: ${element.offsetWidth}px !important;
      height: auto !important;
      margin: 0 !important;
      padding: ${window.getComputedStyle(element).padding} !important;
    `;

    parent.appendChild(clone);
    const height = clone.scrollHeight;
    parent.removeChild(clone);

    this.log('Actual height measurement:', {
      element: element.className,
      height,
      content: element.textContent?.slice(0, 50) + '...',
    });

    return height;
  }

  /**
   * 要素の行の高さを取得する
   * @param element 測定対象の要素
   * @returns 行の高さ(px)
   */
  private getLineHeight(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    let lineHeight = parseFloat(style.lineHeight);

    if (isNaN(lineHeight)) {
      const fontSize = parseFloat(style.fontSize);
      lineHeight = fontSize * 1.2;
    }

    this.log('Line height:', lineHeight);
    return lineHeight;
  }

  /**
   * 要素のスタイルをリセットする
   * @param contentInner リセット対象の親要素
   */
  private resetStyles(contentInner: HTMLElement): void {
    const titleText = contentInner.querySelector(
      `.${CardLargeLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${CardLargeLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    const labels = contentInner.querySelectorAll(
      `.${CardLargeLineClamp.CLASSES.LABEL}`
    ) as NodeListOf<HTMLElement>;

    if (titleText) {
      titleText.style.removeProperty('--line-clamp');
      titleText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    if (mainText) {
      mainText.style.removeProperty('--line-clamp');
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    labels.forEach((label) => {
      label.classList.remove(this.NO_DISPLAY_CLASS);
      label.style.display = ''; // displayプロパティもリセット
    });
  }

  /**
   * 要素の行数制限を計算して適用する
   * @param contentInner 計算対象の親要素
   */
  private calculateLineClamp(contentInner: HTMLElement): void {
    // コンテンツ全体の高さ
    const contentInnerHeight = contentInner.offsetHeight;
    this.log('Content inner height:', contentInnerHeight);

    // 各要素の取得
    const titleText = contentInner.querySelector(
      `.${CardLargeLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${CardLargeLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    const labels = Array.from(
      contentInner.querySelectorAll(`.${CardLargeLineClamp.CLASSES.LABEL}`)
    ) as HTMLElement[];

    // タイトルまたは本文が存在しない場合は処理を終了
    if (!titleText || !mainText) return;

    // タイトルの行高さと実際の高さを取得
    const titleTextLineHeight = this.getLineHeight(titleText);
    const actualTitleHeight = this.getActualHeight(titleText);

    // タイトルのmargin-top, margin-bottomを取得
    const titleStyle = window.getComputedStyle(titleText);
    const titleMarginTop = parseFloat(titleStyle.marginTop);
    const titleMarginBottom = parseFloat(titleStyle.marginBottom);

    // 本文の行高さを取得
    const mainTextLineHeight = this.getLineHeight(mainText);

    // 本文のマージンとパディングを取得
    const mainStyle = window.getComputedStyle(mainText);
    const mainMarginTop = parseFloat(mainStyle.marginTop);
    const mainPaddingTop = parseFloat(mainStyle.paddingTop);
    const mainMarginBottom = parseFloat(mainStyle.marginBottom);

    // --- ここからpタグの個別マージンを考慮する処理 ---
    // mainText内の全pタグのmarginTopとmarginBottomを合算する
    const paragraphs = Array.from(
      mainText.getElementsByTagName('p')
    ) as HTMLElement[];
    let extraParagraphMargin = 0;
    paragraphs.forEach((p) => {
      const pStyle = window.getComputedStyle(p);
      extraParagraphMargin +=
        parseFloat(pStyle.marginTop) + parseFloat(pStyle.marginBottom);
    });
    // --- ここまでpタグの余白計算 ---

    // 利用可能な高さを計算
    const availableHeightForMainAndLabels =
      contentInnerHeight -
      titleMarginTop -
      actualTitleHeight -
      titleMarginBottom -
      mainMarginTop -
      mainPaddingTop -
      mainMarginBottom -
      extraParagraphMargin;

    this.log('Space calculation:', {
      contentInnerHeight,
      titleMarginTop,
      actualTitleHeight,
      titleMarginBottom,
      mainMarginTop,
      mainPaddingTop,
      mainMarginBottom,
      extraParagraphMargin,
      availableHeightForMainAndLabels,
    });

    // タイトルの表示制御
    if (actualTitleHeight > titleTextLineHeight) {
      const titleMaxLines = Math.floor(actualTitleHeight / titleTextLineHeight);
      if (titleMaxLines <= 0) {
        titleText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        titleText.style.setProperty('--line-clamp', titleMaxLines.toString());
      }
    }

    // 本文の表示制御
    const actualMainHeight = this.getActualHeight(mainText);
    if (actualMainHeight > availableHeightForMainAndLabels) {
      const mainMaxLines = Math.floor(
        availableHeightForMainAndLabels / mainTextLineHeight
      );
      if (mainMaxLines <= 0) {
        mainText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        mainText.style.setProperty('--line-clamp', mainMaxLines.toString());
        mainText.classList.remove(this.NO_DISPLAY_CLASS);
        this.log('Main text line clamp:', mainMaxLines);
      }
    } else {
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }

    // ラベルの表示制御
    const labelsContainer = contentInner.querySelector(
      `.${CardLargeLineClamp.CLASSES.LABELS}`
    ) as HTMLElement;
    if (!labelsContainer) return;

    // ラベルの合計高さとマージンを計算
    let labelsTotalHeight = 0;
    let labelsTotalMarginTop = 0;

    labels.forEach((label) => {
      const labelLineHeight = this.getLineHeight(label);
      const actualLabelHeight = this.getActualHeight(label);
      const labelStyle = window.getComputedStyle(label);
      const labelMarginTop = parseFloat(labelStyle.marginTop);
      labelsTotalHeight += actualLabelHeight;
      labelsTotalMarginTop += labelMarginTop;
    });

    // 本文とラベルに利用可能な高さを計算
    const availableHeightForLabels =
      availableHeightForMainAndLabels -
      (actualMainHeight > availableHeightForMainAndLabels
        ? Math.floor(availableHeightForMainAndLabels / mainTextLineHeight) *
          mainTextLineHeight
        : actualMainHeight);

    this.log('Available height for labels:', availableHeightForLabels);

    if (availableHeightForLabels <= 0) {
      labels.forEach((label) => label.classList.add(this.NO_DISPLAY_CLASS));
      return;
    }

    // ラベルを順番に表示し、利用可能な高さ内に収まるかチェック
    let accumulatedHeight = 0;
    labels.forEach((label) => {
      const labelStyle = window.getComputedStyle(label);
      const labelMarginTop = parseFloat(labelStyle.marginTop);
      const labelHeight = this.getActualHeight(label);

      if (
        accumulatedHeight + labelMarginTop + labelHeight <=
        availableHeightForLabels
      ) {
        accumulatedHeight += labelMarginTop + labelHeight;
        label.classList.remove(this.NO_DISPLAY_CLASS);
      } else {
        label.classList.add(this.NO_DISPLAY_CLASS);
      }
    });
  }

  /**
   * リサイズイベントのハンドラー
   * @param entries リサイズ対象の要素リスト
   */
  private handleResize(entries: ResizeObserverEntry[]): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }

    this.resizeTimeout = window.requestAnimationFrame(() => {
      entries.forEach((entry) => {
        const contentInner = entry.target as HTMLElement;
        this.resetStyles(contentInner);
        this.calculateLineClamp(contentInner);
      });
      this.resizeTimeout = null;
    });
  }

  /**
   * 全ての要素の行数制限を更新する
   */
  private updateAllLineClamps(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardLargeLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resetStyles(contentInner);
      this.calculateLineClamp(contentInner);
    });
  }

  /**
   * デバッグログを出力する
   * @param args ログ出力する引数
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[LineClamp]', ...args);
    }
  }

  /**
   * インスタンスを破棄する
   */
  public destroy(): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeObserver.disconnect();
  }

  /**
   * 行数制限を再計算する
   */
  public recalculate(): void {
    this.updateAllLineClamps();
  }
}
